import React, { useState, useEffect } from 'react';
import { Save, X, Upload, Loader2, Calculator, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { purchaseService } from '../../services/purchaseService';
import { productService } from '../../services/productService';
import { notificationService } from '../../services/notificationService';
import { PurchaseWithProduct, PurchaseFormData } from '../../types/purchase';
import { Product } from '../../types/product';
// import { uploadImage, CloudinaryUploadResult } from '../../services/cloudinaryService'; // Ya no se necesita aquí

// Exportar para que sea considerado un módulo
export {};

interface PurchaseFormProps {
  purchase?: PurchaseWithProduct | null;
  onSave: () => void;
  onCancel: () => void;
}

const PurchaseForm = ({ purchase, onSave, onCancel }: PurchaseFormProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<PurchaseFormData>({
    product_id: 0,
    quantity: 1,
    unit_cost: 0,
    shipping_cost: 0,
    other_expenses: 0,
    purchase_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [calculatedCosts, setCalculatedCosts] = useState({
    totalCost: 0,
    finalUnitCost: 0
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (purchase) {
      setFormData({
        product_id: purchase.product_id,
        quantity: purchase.quantity,
        unit_cost: purchase.unit_cost,
        shipping_cost: purchase.shipping_cost,
        other_expenses: purchase.other_expenses,
        purchase_date: purchase.purchase_date,
        notes: '',
        // No incluimos reference_document aquí porque solo se usa para nuevos archivos
        // El documento existente se maneja a través de reference_document_url
      });
    }
  }, [purchase]);

  useEffect(() => {
    // Calcular costos automáticamente cuando cambian los valores
    const totalCost = (formData.unit_cost * formData.quantity) + formData.shipping_cost + formData.other_expenses;
    const finalUnitCost = formData.quantity > 0 ? totalCost / formData.quantity : 0;
    
    setCalculatedCosts({
      totalCost,
      finalUnitCost
    });
  }, [formData.quantity, formData.unit_cost, formData.shipping_cost, formData.other_expenses]);

  const loadProducts = async () => {
    try {
      const data = await productService.getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      notificationService.error('Error al cargar productos', 'No se pudieron cargar los productos. Por favor, recargue la página.');
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData.product_id === 0) {
      newErrors.product_id = 'Debe seleccionar un producto';
    }

    if (formData.quantity <= 0) {
      newErrors.quantity = 'La cantidad debe ser mayor a 0';
    }

    if (formData.unit_cost <= 0) {
      newErrors.unit_cost = 'El costo unitario debe ser mayor a 0';
    }

    if (formData.shipping_cost < 0) {
      newErrors.shipping_cost = 'El costo de envío no puede ser negativo';
    }

    if (formData.other_expenses < 0) {
      newErrors.other_expenses = 'Los otros gastos no pueden ser negativos';
    }

    if (!formData.purchase_date) {
      newErrors.purchase_date = 'La fecha de compra es obligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      let success = false;
      if (purchase) {
        // Editar compra existente
        const updatedPurchase = await purchaseService.updatePurchase(purchase.id, formData);
        success = updatedPurchase !== null;
      } else {
        // Agregar nueva compra
        const newPurchase = await purchaseService.addPurchase(formData);
        success = newPurchase !== null;
      }

      if (success) {
        notificationService.success(
          'Compra guardada exitosamente', 
          purchase ? 'La compra ha sido actualizada correctamente.' : 'La compra ha sido registrada correctamente.'
        );
        onSave();
      } else {
        notificationService.error('Error al guardar compra', 'Por favor, verifique los datos e intente nuevamente.');
      }
    } catch (error) {
      console.error('Error al guardar compra:', error);
      notificationService.error('Error al guardar compra', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validar tipo de archivo
    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      notificationService.warning('Archivo no válido', 'Por favor, seleccione un archivo de imagen o PDF válido.');
      return;
    }

    // Validar tamaño (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notificationService.warning('Archivo demasiado grande', 'El documento no debe superar los 10MB.');
      return;
    }

    setIsUploading(true);
    try {
      // Solo guardamos el archivo en el estado, no lo subimos todavía
      // La subida se hará cuando se guarde la compra para evitar duplicación
      setFormData(prev => ({
        ...prev,
        reference_document: file
      }));

      notificationService.success('Documento seleccionado', 'El documento se subirá cuando guarde la compra.');
    } catch (error) {
      console.error('Error al procesar el documento:', error);
      notificationService.error('Error al procesar documento', 'Ha ocurrido un error al procesar el documento. Por favor, intente nuevamente.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));

    // Limpiar error cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const selectedProduct = products.find(p => p.id === formData.product_id);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
          {purchase ? 'Editar Compra' : 'Registrar Nueva Compra'}
        </h3>
        <Button variant="outline" size="sm" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Selección de producto */}
        <div>
          <label htmlFor="product_id" className="block text-sm font-medium text-gray-700 mb-2">
            Producto *
          </label>
          <select
            id="product_id"
            name="product_id"
            value={formData.product_id}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.product_id ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="0">Seleccione un producto</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>
                {product.name} - {product.category} (Stock actual: {product.stock})
              </option>
            ))}
          </select>
          {errors.product_id && (
            <p className="mt-1 text-sm text-red-600">{errors.product_id}</p>
          )}
        </div>

        {/* Información del producto seleccionado */}
        {selectedProduct && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Package className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900">{selectedProduct.name}</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Categoría: {selectedProduct.category} | Stock actual: {selectedProduct.stock} unidades
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Cantidad */}
          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
              Cantidad *
            </label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
              min="1"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.quantity ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="1"
            />
            {errors.quantity && (
              <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
            )}
          </div>

          {/* Fecha de compra */}
          <div>
            <label htmlFor="purchase_date" className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Compra *
            </label>
            <input
              type="date"
              id="purchase_date"
              name="purchase_date"
              value={formData.purchase_date}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.purchase_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.purchase_date && (
              <p className="mt-1 text-sm text-red-600">{errors.purchase_date}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {/* Costo unitario */}
          <div>
            <label htmlFor="unit_cost" className="block text-sm font-medium text-gray-700 mb-2">
              Costo Unitario (USD) *
            </label>
            <input
              type="number"
              id="unit_cost"
              name="unit_cost"
              value={formData.unit_cost}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.unit_cost ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.unit_cost && (
              <p className="mt-1 text-sm text-red-600">{errors.unit_cost}</p>
            )}
          </div>

          {/* Costo de envío */}
          <div>
            <label htmlFor="shipping_cost" className="block text-sm font-medium text-gray-700 mb-2">
              Costo de Envío (USD)
            </label>
            <input
              type="number"
              id="shipping_cost"
              name="shipping_cost"
              value={formData.shipping_cost}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.shipping_cost ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.shipping_cost && (
              <p className="mt-1 text-sm text-red-600">{errors.shipping_cost}</p>
            )}
          </div>

          {/* Otros gastos */}
          <div>
            <label htmlFor="other_expenses" className="block text-sm font-medium text-gray-700 mb-2">
              Otros Gastos (USD)
            </label>
            <input
              type="number"
              id="other_expenses"
              name="other_expenses"
              value={formData.other_expenses}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.other_expenses ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.other_expenses && (
              <p className="mt-1 text-sm text-red-600">{errors.other_expenses}</p>
            )}
          </div>
        </div>

        {/* Resumen de costos calculados */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <Calculator className="w-5 h-5 text-gray-600 mr-2" />
            <h4 className="font-medium text-gray-900">Resumen de Costos</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal ({formData.quantity} unidades):</span>
                <span className="font-medium">{formatCurrency(formData.unit_cost * formData.quantity)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Costo de envío:</span>
                <span className="font-medium">{formatCurrency(formData.shipping_cost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Otros gastos:</span>
                <span className="font-medium">{formatCurrency(formData.other_expenses)}</span>
              </div>
            </div>
            <div>
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-900 font-medium">Costo total:</span>
                  <span className="font-bold text-lg">{formatCurrency(calculatedCosts.totalCost)}</span>
                </div>
                <div className="bg-primary-50 p-3 rounded-lg mt-2">
                  <div className="flex justify-between">
                    <span className="text-primary-900 font-medium">Costo final por unidad:</span>
                    <span className="font-bold text-primary-900 text-lg">
                      {formatCurrency(calculatedCosts.finalUnitCost)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Documento de referencia */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Documento de Referencia (Opcional)
          </label>
          
          {/* Mostrar documento existente si se está editando */}
          {purchase && purchase.reference_document_url && (
            <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Documento actual:</span>
                  <span className="text-sm text-gray-600">{purchase.reference_document}</span>
                </div>
                <a
                  href={purchase.reference_document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary-600 hover:text-primary-800 underline"
                >
                  Ver documento
                </a>
              </div>
            </div>
          )}
          
          <div className="flex items-center space-x-4">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleDocumentUpload}
                disabled={isUploading}
                className="hidden"
              />
              <div className={`flex items-center px-4 py-2 border-2 border-dashed rounded-lg transition-colors ${
                isUploading
                  ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                  : 'border-primary-300 hover:border-primary-500 bg-primary-50 cursor-pointer'
              }`}>
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span className="text-sm">Procesando...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    <span className="text-sm">Seleccionar documento</span>
                  </>
                )}
              </div>
            </label>
            
            {formData.reference_document && (
              <div className="flex items-center text-sm text-gray-600">
                <span className="truncate max-w-[200px]">
                  {formData.reference_document instanceof File
                    ? formData.reference_document.name
                    : 'Documento seleccionado'}
                </span>
              </div>
            )}
          </div>
          <div className="mt-2 space-y-1">
            <p className="text-xs text-gray-500">
              Formatos: JPG, PNG, GIF, PDF. Máximo 10MB
            </p>
            <p className="text-xs text-blue-600">
              💡 El documento se subirá automáticamente cuando guarde la compra
            </p>
          </div>
        </div>

        {/* Notas */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Notas (Opcional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Notas adicionales sobre la compra..."
          />
        </div>

        {/* Botones */}
        <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-3 sm:space-y-0 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="btn-primary w-full sm:w-auto"
            disabled={isLoading || isUploading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {purchase ? 'Actualizar Compra' : 'Registrar Compra'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PurchaseForm;